import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Languages, Mic, Volume2, Radio, Headphones, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import TolokyAdminPanel from "@/components/comms/toloky/TolokyAdminPanel";
import TolokyConsentDialog from "@/components/comms/toloky/TolokyConsentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const VOICE_OPTIONS = [
  { value: "ava", label: "Ava (Female)" },
  { value: "andrew", label: "Andrew (Male)" },
  { value: "fable", label: "Fable (Neutral)" },
];

const MODE_OPTIONS = [
  { value: "consecutive", label: "Consecutive (turn-based)" },
  { value: "realtime", label: "Real-time (simultaneous)" },
];

export default function TolokySettings() {
  const [settings, setSettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    Promise.all([fetchSettings(), fetchLanguages()]).finally(() => setLoading(false));
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/settings`, {
        headers: getHeaders(), withCredentials: true,
      });
      setSettings(res.data);
    } catch {}
  };

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/languages`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLanguages(res.data.languages || []);
    } catch {}
  };

  const updateSetting = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try {
      await axios.put(`${API}/comms/toloky/settings`, { [key]: value }, {
        headers: getHeaders(), withCredentials: true,
      });
    } catch {
      fetchSettings();
    }
  };

  const handleVoiceSimToggle = async (enabled) => {
    if (enabled && !settings?.voice_simulation_consent) {
      setShowConsent(true);
      return;
    }
    updateSetting("voice_simulation_enabled", enabled);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/comms-centre"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to COMMS Centre
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 tracking-wide">
              TOLOKY INTERPRETER
            </h1>
            <p className="text-sm text-slate-500">
              AI-powered real-time translation for COMMS Centre meetings
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <Tabs defaultValue="preferences">
        <TabsList className="rounded-none border-b border-slate-200 w-full justify-start bg-transparent gap-6">
          <TabsTrigger
            value="preferences"
            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-500 data-[state=active]:text-violet-700 pb-3"
          >
            My Preferences
          </TabsTrigger>
          <TabsTrigger
            value="admin"
            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-500 data-[state=active]:text-violet-700 pb-3"
          >
            Admin Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="preferences" className="mt-6">
          {settings && (
            <div className="bg-white border border-slate-200 p-6 space-y-6 max-w-lg">
              {/* Language Selection */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Languages className="w-3 h-3" /> Language
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">I speak</Label>
                    <Select
                      value={settings.source_language || "auto"}
                      onValueChange={(v) => updateSetting("source_language", v === "auto" ? "" : v)}
                    >
                      <SelectTrigger className="h-9 text-sm rounded-none">
                        <SelectValue placeholder="Auto-detect" />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-none max-h-60">
                        <SelectItem value="auto">Auto-detect</SelectItem>
                        {languages.map((l) => (
                          <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">I want to hear</Label>
                    <Select
                      value={settings.target_language}
                      onValueChange={(v) => updateSetting("target_language", v)}
                    >
                      <SelectTrigger className="h-9 text-sm rounded-none">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-none max-h-60">
                        {languages.map((l) => (
                          <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Voice Simulation */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Mic className="w-3 h-3" /> Voice
                </h3>
                <div className="bg-app border border-slate-200 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Simulate my voice</p>
                      <p className="text-[11px] text-slate-500">
                        Mimics your tone and style in translation
                      </p>
                    </div>
                    <Switch
                      checked={settings.voice_simulation_enabled}
                      onCheckedChange={handleVoiceSimToggle}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Default voice</Label>
                    <Select
                      value={settings.default_voice}
                      onValueChange={(v) => updateSetting("default_voice", v)}
                    >
                      <SelectTrigger className="h-9 text-sm rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-none">
                        {VOICE_OPTIONS.map((v) => (
                          <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Interpretation Mode */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Radio className="w-3 h-3" /> Mode
                </h3>
                <Select
                  value={settings.interpretation_mode}
                  onValueChange={(v) => updateSetting("interpretation_mode", v)}
                >
                  <SelectTrigger className="h-9 text-sm rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none">
                    {MODE_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Volume Controls */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Headphones className="w-3 h-3" /> Audio Balance
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 flex items-center gap-1">
                        <Volume2 className="w-3 h-3" /> Interpreter volume
                      </span>
                      <span className="text-slate-800 font-mono">{settings.interpreter_volume}%</span>
                    </div>
                    <Slider
                      value={[settings.interpreter_volume]}
                      onValueChange={([v]) => updateSetting("interpreter_volume", v)}
                      max={100}
                      step={1}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 flex items-center gap-1">
                        <Headphones className="w-3 h-3" /> Original audio
                      </span>
                      <span className="text-slate-800 font-mono">{settings.original_volume}%</span>
                    </div>
                    <Slider
                      value={[settings.original_volume]}
                      onValueChange={([v]) => updateSetting("original_volume", v)}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="admin" className="mt-6">
          <TolokyAdminPanel />
        </TabsContent>
      </Tabs>

      <TolokyConsentDialog
        open={showConsent}
        onOpenChange={setShowConsent}
        onComplete={(granted) => {
          if (granted) {
            updateSetting("voice_simulation_enabled", true);
            updateSetting("voice_simulation_consent", true);
          }
        }}
      />
    </div>
  );
}
